/* Copyright (c) 2025 Xperi. All Rights Reserved. */

/**
 * This module defines the `PreviewComponent` class and related methods for handling various
 * preview functionalities.
 * It imports necessary kernels, types, and interactors to manage content previews, recording
 * options, and other preview-related actions.
 *
 * Key functionalities include:
 * - Creating labels for recording actions (start/stop recording) and "keep until" settings.
 * - Interacting with services and locale for fetching and formatting data.
 * - Handling different types of preview kernels such as content preview, offer preview,
 * recording preview, and unified collection preview.
 * - Providing methods to retrieve trickplay restriction assets and recording options preview
 * information.
 *
 * The module integrates with various components and services to provide a comprehensive
 * preview management system.
 */

import {
    ContentPreviewKernel,
    InfoCardKernel,
    OfferPreviewKernel,
    OnePassOptionsPreviewKernel,
    PreviewKernel,
    RecordingOptionsPreviewKernel,
    RecordingPreviewKernel,
    UnifiedCollectionAndOnePassOptionsPreviewKernel,
    UnifiedCollectionPreviewKernel,
    VodOfferPreviewKernel,
} from 'client-code/actions';
import { PreviewGroup, ScreenType } from 'client-code/previews';
import {
    KeepUntilValueItem,
    StartRecordingMinutesEarly,
    StopRecordingMinutesLate,
    TimeAndChannelValueItem,
} from 'client-code/recordings';
import {
    ContentVodItemKernel,
    OfferVodItemKernel,
    UnifiedCollectionVodItemKernel,
} from 'client-code/vod-browse';

import { NdvrInteractor, ServicesClientImpl, VodInteractor } from '../interactors';
import { Locale } from '../locale';
import {
    TContentPreview,
    TExtendedRatingLimits,
    TOfferPreview,
    TOnePassOptions,
    TOnePassPreviewItem,
    TPreviewKernel,
    TRecordingOptionsPreview,
    TUnifiedCollectionPreview,
} from '../types';
import { TVodOfferPreview } from '../types/TVodOfferPreview';
import { DateTime } from '../utils';
import { PreviewComponentAssets } from './PreviewComponentAssets';

export class PreviewComponent {
    private mPreviewComponentAssets: PreviewComponentAssets;

    private mNdvrInteractor: NdvrInteractor;

    private mPreviewOrInfoCardKernel:
        | PreviewKernel
        | InfoCardKernel
        | TPreviewKernel
        | ContentVodItemKernel
        | UnifiedCollectionVodItemKernel;

    private mServicesInteractor: ServicesClientImpl;

    private mVodInteractor: VodInteractor;

    public constructor(
        previewOrInfoCardKernel:
            | PreviewKernel
            | InfoCardKernel
            | TPreviewKernel
            | ContentVodItemKernel
            | UnifiedCollectionVodItemKernel,
    ) {
        this.mPreviewOrInfoCardKernel = previewOrInfoCardKernel;
        this.mServicesInteractor = new ServicesClientImpl();
        this.mNdvrInteractor = new NdvrInteractor();
        this.mVodInteractor = new VodInteractor();
        this.mPreviewComponentAssets = new PreviewComponentAssets();
    }

    /**
     * Creates a label for the "keep until" setting based on the provided value.
     *
     * @param keepUntil - The value indicating how long to keep the item.
     * @returns A string representing the label for the "keep until" setting.
     */
    // eslint-disable-next-line class-methods-use-this
    private createKeepUntilLabel(keepUntil?: string): string {
        let retValue: string;
        switch (keepUntil) {
            case KeepUntilValueItem.AsLongAsPossible:
                retValue = Locale.getString(
                    PreviewComponentAssets.StringAssets.PREVIEW_KEEP_UNTIL_AS_LONG_AS_POSSIBLE,
                );
                break;

            case KeepUntilValueItem.SpaceNeeded:
                retValue = Locale.getString(
                    PreviewComponentAssets.StringAssets.PREVIEW_KEEP_UNTIL_SPACE_NEEDED,
                );
                break;

            default:
                retValue = '';
        }
        return retValue;
    }

    /**
     * Creates a label for starting or stopping recording based on the provided recording value.
     *
     * @param recordingValue - An object containing the type of recording action and the number
     * of minutes.
     * @returns A string representing the label for the recording action.
     */
    private createStartOrStopRecordingLabel(
        recordingValue: StartRecordingMinutesEarly | StopRecordingMinutesLate = {
            minutes: 0,
            type: '',
        },
    ): string {
        let displayText: string = '';
        const { minutes = 0, type } = recordingValue;

        if (type === 'StartRecordingOnTime' || type === 'StopRecordingOnTime') {
            displayText = this.mPreviewComponentAssets.getStringAssestById(
                PreviewComponentAssets.StringAssets.PREVIEW_LABEL_ON_TIME,
            );
        }

        if (type === 'StartRecordingMinutesEarly') {
            displayText = this.mPreviewComponentAssets.getStartRecordingMinutesEarlyString(minutes);
        }

        if (type === 'StopRecordingMinutesLate') {
            displayText = this.mPreviewComponentAssets.getStopRecordingMinutesLateString(minutes);
        }

        return displayText;
    }

    /**
     * Creates a label combining time and channel information.
     *
     * @param timeAndChannel - An optional object containing channel ID and date-time information.
     * @returns A promise that resolves to a string representing the time and channel label.
     */
    private async createTimeAndChannelLabel(
        timeAndChannel?: TimeAndChannelValueItem,
    ): Promise<string> {
        if (!timeAndChannel) return '';
        const { channelId, dateTime } = timeAndChannel;
        const dt = new DateTime(dateTime);
        let displayText = `${dt.strftime('%a %m/%d')} ${dt.to12HourClock()}`;
        if (channelId) {
            const channelNumber = channelId.split('.')[1];
            try {
                const channel =
                    await this.mServicesInteractor.getChannelFromChannelNumber(channelNumber);
                displayText += ` ${channel.channelNumber} ${channel.callSign}`;
            } catch (error) {
                // silently fail, do nothing
            }
        }
        return displayText;
    }

    /**
     * Retrieves the `PreviewComponentAssets` instance.
     *
     * @returns An instance of `PreviewComponentAssets`.
     */
    public getPreviewComponentAssets(): PreviewComponentAssets {
        return this.mPreviewComponentAssets;
    }

    /**
     * Retrieves the content preview information.
     *
     * @returns A promise that resolves to an object containing the content preview information.
     */
    public async getContentPreviewInfo(): Promise<TContentPreview> {
        const { contentId } = this.mPreviewOrInfoCardKernel as ContentPreviewKernel;
        if (!contentId) {
            throw new Error('Invalid contentId.');
        }
        return this.mServicesInteractor.getContentPreviewV2ForContentId(contentId);
    }

    /**
     * Retrieves the content preview information.
     *
     * @returns A promise that resolves to an object containing the content preview information.
     */
    public async getContentPreviewV3Info(): Promise<TContentPreview> {
        const { contentId, collapsedVodItemId, contentSupplierPartnerId, partnerId } = this
            .mPreviewOrInfoCardKernel as ContentPreviewKernel;
        if (!contentId) {
            throw new Error('Invalid contentId.');
        }
        return this.mVodInteractor.fetchContentPreviewV3({
            contentId,
            collapsedVodItemId,
            contentSupplierPartnerId,
            partnerId,
        });
    }

    /**
     * Retrieves the vod content preview information.
     *
     * @returns A promise that resolves to an object containing the content preview information.
     */
    public async getVodContentPreviewInfo(groups: Array<PreviewGroup>): Promise<TContentPreview> {
        const { contentId, contentSupplierPartnerId, collapsedVodItemId, partnerId } = this
            .mPreviewOrInfoCardKernel as ContentVodItemKernel;
        if (!contentId) {
            throw new Error('Invalid contentId.');
        }
        return this.mVodInteractor.fetchContentPreviewV3({
            contentId,
            partnerId,
            collapsedVodItemId,
            groups,
            contentSupplierPartnerId,
        });
    }

    /**
     * Retrieves the offer preview information.
     *
     * @returns A promise that resolves to an object containing the offer preview information.
     */
    public getOfferPreviewInfo(): Promise<TOfferPreview> {
        const { offerId } = this.mPreviewOrInfoCardKernel as OfferPreviewKernel;
        if (!offerId) {
            throw new Error('Invalid offerId.');
        }
        return this.mNdvrInteractor.fetchOfferPreview(offerId, ScreenType.All);
    }

    /**
     * Retrieves the one-pass options preview information.
     *
     * @returns A promise that resolves to an object containing the one-pass options preview
     * information.
     */
    public getOnePassOptionsPreviewInfo(): Promise<TOnePassPreviewItem> {
        const { unifiedCollectionId } = this
            .mPreviewOrInfoCardKernel as OnePassOptionsPreviewKernel;
        if (!unifiedCollectionId) {
            throw new Error('Invalid unifiedCollectionId.');
        }
        return this.mNdvrInteractor.getOnePassPreviewItemByUCId({
            screenType: 'All',
            unifiedCollectionId,
        });
    }

    /**
     * Retrieves the recording options preview information.
     *
     * @returns A promise that resolves to an object containing the recording options preview
     * information.
     */
    public async getRecordingOptionsPreviewInfo(): Promise<TRecordingOptionsPreview> {
        const { contentId, offerId } = this
            .mPreviewOrInfoCardKernel as RecordingOptionsPreviewKernel;
        if (!(contentId && offerId)) {
            throw new Error('Invalid contentId or offerId.');
        }

        const tRecordingValuesWithDisplayOptions = await this.mNdvrInteractor
            .getRecordingOfferIdScheduleRequestModel(contentId, offerId)
            .send();
        const { recordingValues } =
            tRecordingValuesWithDisplayOptions.recordingValuesWithDisplayOptions;

        const timeAndChannelLabel = await this.createTimeAndChannelLabel(
            recordingValues?.timeAndChannel,
        );
        const retValue: TRecordingOptionsPreview = {
            timeAndChannelLabel,
            keepUntilLabel: this.createKeepUntilLabel(recordingValues?.keepUntil),
            startRecordingLabel: this.createStartOrStopRecordingLabel(
                recordingValues?.startRecordingEarly,
            ),
            stopRecordingLabel: this.createStartOrStopRecordingLabel(
                recordingValues?.stopRecordingLate,
            ),
        };

        return retValue;
    }

    /**
     * Retrieves the recording preview information.
     *
     * @returns A promise that resolves to an object containing the recording preview information.
     */
    public getRecordingPreviewInfo(): Promise<TOfferPreview> {
        const { recordingId, channelNumber = '' } = this
            .mPreviewOrInfoCardKernel as RecordingPreviewKernel;
        if (!recordingId) {
            throw new Error('Invalid recordingId.');
        }
        return this.mNdvrInteractor.fetchRecordingPreview({
            applicationFeatureArea: 'all',
            recordingId,
            channelNumber,
        });
    }

    /**
     * Retrieves the unified collection and OnePass options preview information.
     *
     * @returns A promise that resolves to an object containing the unified collection and OnePass
     * options preview information.
     */
    public getUnifiedCollectionAndOnePassOptionsPreviewInfo(): Promise<TOnePassOptions> {
        const { unifiedCollectionId } = this
            .mPreviewOrInfoCardKernel as UnifiedCollectionAndOnePassOptionsPreviewKernel;
        if (!unifiedCollectionId) {
            throw new Error('Invalid unifiedCollectionId.');
        }
        return this.mNdvrInteractor.getOnePassOptions(unifiedCollectionId);
    }

    /**
     * Retrieves the unified collection preview information.
     *
     * @returns A promise that resolves to an object containing the unified collection preview
     * information.
     */
    public getUnifiedCollectionPreviewInfo(): Promise<TUnifiedCollectionPreview> {
        const { contentSupplierPartnerId, partnerId, unifiedCollectionId } = this
            .mPreviewOrInfoCardKernel as UnifiedCollectionPreviewKernel;
        if (!unifiedCollectionId) {
            throw new Error('Invalid unifiedCollectionId.');
        }
        return this.mNdvrInteractor.getPreviewForUCId({
            contentSupplierPartnerId,
            partnerId,
            unifiedCollectionId,
        });
    }

    /**
     * Retrieves the Vod unified collection preview information.
     *
     * @returns A promise that resolves to an object containing the unified collection preview
     * information.
     */

    public getVodUnifiedCollectionPreviewInfo(
        groups?: Array<PreviewGroup>,
    ): Promise<TUnifiedCollectionPreview> {
        const { contentSupplierPartnerId, partnerId, unifiedCollectionId } = this
            .mPreviewOrInfoCardKernel as UnifiedCollectionVodItemKernel;
        if (!unifiedCollectionId) {
            throw new Error('Invalid unifiedCollectionId.');
        }
        return this.mNdvrInteractor.getPreviewForUCId({
            contentSupplierPartnerId,
            partnerId,
            groups,
            unifiedCollectionId,
        });
    }

    /**
     * Retrieves the VOD offer preview information.
     *
     * @returns A promise that resolves to an object containing the VOD offer preview information.
     */
    public getVodOfferPreviewInfo(): Promise<TOfferPreview> {
        const { offerId } = this.mPreviewOrInfoCardKernel as VodOfferPreviewKernel;
        if (!offerId) {
            throw new Error('Invalid offerId.');
        }
        return this.mServicesInteractor.fetchOfferPreviewV2(offerId, ScreenType.All);
    }

    /**
     * Retrieves the VOD offer preview information.
     *
     * @returns A promise that resolves to an object containing the VOD offer preview information.
     */
    public getVodOfferPreviewV2Info(groups?: Array<PreviewGroup>): Promise<TVodOfferPreview> {
        const { offerId } = this.mPreviewOrInfoCardKernel as OfferVodItemKernel;
        if (!offerId) {
            throw new Error('Invalid offerId.');
        }
        return this.mVodInteractor.fetchVodOfferPreviewV2({
            vodOfferId: offerId,
            screenType: ScreenType.All,
            groups,
        });
    }

    /**
     * Retrieves the parental rating names based on the internal ratings.
     *
     * @param internalRatings - An array of internal ratings.
     * @returns An array of strings representing the parental rating names.
     */
    public static getParentalRatingNames(internalRatings): Array<string> {
        const ratingService = new ServicesClientImpl();
        const internalRatingDb: TExtendedRatingLimits = ratingService.getInternalRatingsData();
        const ratingNames = internalRatings.map((rating) => {
            const ratingValues = internalRatingDb.ratingValue || [];
            for (let index = 0; index < ratingValues.length; index += 1) {
                const ratingValue = ratingValues[index];
                if (
                    ratingValue.ratingTypeId === rating.typeId &&
                    ratingValue.ratingValueId === rating.valueId
                ) {
                    return ratingValue.name;
                }
            }
            return '';
        });

        return ratingNames || [];
    }

    public static getHighestParentalRatingName(internalRatings): string {
        const ratingService = new ServicesClientImpl();
        const internalRatingDb: TExtendedRatingLimits = ratingService.getInternalRatingsData();
        const ratingValues = internalRatingDb.ratingValue || [];

        let highestRating: { name: string; level: number } | null = null as {
            name: string;
            level: number;
        } | null;

        internalRatings.forEach((rating) => {
            const ratingValue = ratingValues.find(
                (rv) => rv.ratingTypeId === rating.typeId && rv.ratingValueId === rating.valueId,
            );

            if (ratingValue) {
                if (!highestRating || Number(ratingValue.level) > highestRating.level) {
                    highestRating = {
                        name: ratingValue.name,
                        level: Number(ratingValue.level),
                    };
                }
            }
        });
        return highestRating ? highestRating.name : '';
    }
}
